import React, { useEffect, useState } from "react";
import { AppShell } from "../../components/Sidebar";
import { api } from "../../lib/api";
import { Plus, GraduationCap, Copy, Check, X, Pencil, Trash2, Eye, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const initials = (n = "U") => n.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

const SUBJECTS  = ["Science", "Maths", "Maths - Ganita Prakash I", "Maths - Ganita Prakash II", "Maths - Ganita Manjari I", "Maths - Ganita Manjari II", "English", "Hindi", "Social Studies", "Computer Science"];
const GRADES    = ["6", "7", "8", "9", "10"];
const DIVISIONS = ["A", "B", "C", "D"];

const EMPTY_FORM = {
  school_id: "", full_name: "", email: "", mobile: "", date_of_birth: "",
  subjects: [], grades: [], divisions: [],
};

export default function AdminTeachers() {
  const [items, setItems] = useState([]);
  const [schools, setSchools] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [credView, setCredView] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creds, setCreds] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = () => api.get("/admin/teachers").then((r) => setItems(r.data));

  useEffect(() => {
    load();
    api.get("/admin/schools").then((r) => {
      setSchools(r.data);
      setForm((f) => ({ ...f, school_id: r.data[0]?.id || "" }));
    });
  }, []);

  const startEdit = (t) => {
    setEditing(t);
    setForm({
      school_id: t.school_id, full_name: t.full_name, email: t.email || "",
      mobile: t.mobile || "", date_of_birth: t.date_of_birth || "",
      subjects: t.subjects || [],
      grades: t.grades?.length ? t.grades : ([t.grade].filter(Boolean)),
      divisions: t.divisions?.length ? t.divisions : ([t.division].filter(Boolean)),
    });
    setOpen(true); setCreds(null);
  };
  const startNew = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, school_id: schools[0]?.id || "" });
    setOpen(true); setCreds(null);
  };

  const toggleChip = (key, v) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subjects.length) return toast.error("Pick at least one subject");
    if (!form.grades.length) return toast.error("Pick at least one grade");
    if (!form.divisions.length) return toast.error("Pick at least one division");
    try {
      if (editing) {
        await api.put(`/admin/teachers/${editing.id}`, {
          full_name: form.full_name, email: form.email, mobile: form.mobile,
          subjects: form.subjects, grades: form.grades, divisions: form.divisions,
        });
        toast.success("Teacher updated");
        setOpen(false); setEditing(null); load();
      } else {
        const { data } = await api.post("/admin/teachers", form);
        setCreds(data);
        load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/teachers/${confirmDel.id}`);
      toast.success("Teacher deleted");
      setConfirmDel(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const viewCreds = async (t) => {
    try {
      const { data } = await api.get(`/admin/teachers/${t.id}/credentials`);
      setCredView(data);
    } catch { toast.error("Could not load credentials"); }
  };

  const copy = () => {
    if (!creds) return;
    navigator.clipboard.writeText(`Username: ${creds.username}\nPassword: ${creds.password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <AppShell role="admin">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Teachers</h1>
          <p className="mt-1 text-slate-500">Manage teacher accounts and their assigned subjects, grades and divisions.</p>
        </div>
        <button
          onClick={startNew}
          data-testid="add-teacher-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700"
        >
          <Plus size={16} /> Add Teacher
        </button>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <th className="py-3 pr-4">Teacher</th>
              <th className="py-3 pr-4">Username</th>
              <th className="py-3 pr-4">School</th>
              <th className="py-3 pr-4">Subjects</th>
              <th className="py-3 pr-4">Grades · Divisions</th>
              <th className="py-3 pr-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id} className="border-b border-slate-50 align-top">
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-700">{initials(t.full_name)}</div>
                    <div>
                      <p className="font-medium text-slate-900">{t.full_name}</p>
                      <p className="text-xs text-slate-500">{t.email || "—"} · {t.mobile || "—"}</p>
                    </div>
                  </div>
                </td>
                <td className="py-3 pr-4 font-mono text-xs">{t.username}</td>
                <td className="py-3 pr-4 text-slate-600">{t.school_name}</td>
                <td className="py-3 pr-4">
                  <div className="flex flex-wrap gap-1">
                    {(t.subjects?.length ? t.subjects : ["—"]).map((s) => (
                      <span key={s} className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700">{s}</span>
                    ))}
                  </div>
                </td>
                <td className="py-3 pr-4">
                  <div className="flex flex-wrap gap-1">
                    {(t.grades?.length ? t.grades : [t.grade]).filter(Boolean).map((g) => (
                      <span key={g} className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700">Grade {g}</span>
                    ))}
                    {(t.divisions?.length ? t.divisions : [t.division]).filter(Boolean).map((d) => (
                      <span key={d} className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Div {d}</span>
                    ))}
                  </div>
                </td>
                <td className="py-3 pr-4">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => viewCreds(t)} data-testid={`view-creds-teacher-${t.id}`} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"><Eye size={12} /></button>
                    <button onClick={() => startEdit(t)} data-testid={`edit-teacher-${t.id}`} className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"><Pencil size={12} /></button>
                    <button onClick={() => setConfirmDel(t)} data-testid={`delete-teacher-${t.id}`} className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100"><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="py-12 text-center text-sm text-slate-500">No teachers yet.</p>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-900/60 p-4 backdrop-blur-sm">
          <div className="my-8 w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600"><GraduationCap size={18} /></div>
                <h3 className="text-xl font-bold text-slate-900">{editing ? "Edit Teacher" : "Add Teacher"}</h3>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>

            {!creds ? (
              <form onSubmit={submit} className="mt-5 space-y-4">
                <Field label="School">
                  <select required data-testid="teacher-school" value={form.school_id} onChange={(e) => setForm({ ...form, school_id: e.target.value })} className={inp}>
                    {schools.map((sc) => <option key={sc.id} value={sc.id}>{`${sc.name} (${sc.code})`}</option>)}
                  </select>
                </Field>
                <Field label="Full Name">
                  <input required data-testid="teacher-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={inp} placeholder="e.g. Asha Verma" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Date of Birth (DDMM)">
                    <input data-testid="teacher-dob" maxLength={4} value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className={inp} placeholder="1505" />
                  </Field>
                  <Field label="Mobile">
                    <input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className={inp} placeholder="+91 …" />
                  </Field>
                </div>
                <Field label="Email">
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} />
                </Field>

                <ChipField
                  testidPrefix="subject"
                  label="Subjects (one or more)"
                  values={form.subjects}
                  options={SUBJECTS}
                  onToggle={(v) => toggleChip("subjects", v)}
                  palette="violet"
                />
                <ChipField
                  testidPrefix="grade"
                  label="Grades (one or more)"
                  values={form.grades}
                  options={GRADES}
                  onToggle={(v) => toggleChip("grades", v)}
                  palette="sky"
                />
                <ChipField
                  testidPrefix="division"
                  label="Divisions (one or more)"
                  values={form.divisions}
                  options={DIVISIONS}
                  onToggle={(v) => toggleChip("divisions", v)}
                  palette="amber"
                />

                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => setOpen(false)} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
                  <button data-testid="submit-teacher" className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700">Create Teacher</button>
                </div>
              </form>
            ) : (
              <div className="mt-5 space-y-3">
                <div className="rounded-xl bg-emerald-50 p-4 text-sm">
                  <p className="font-semibold text-emerald-700">Teacher created!</p>
                  <p className="mt-1 text-emerald-700/80">Share these credentials with the teacher. Save them now — you won't see the password again.</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 font-mono text-sm">
                  <p>Username: <span className="font-semibold">{creds.username}</span></p>
                  <p>Password: <span className="font-semibold">{creds.password}</span></p>
                </div>
                <div className="flex justify-end gap-3">
                  <button onClick={copy} className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                  </button>
                  <button onClick={() => { setOpen(false); setCreds(null); setEditing(null); setForm(EMPTY_FORM); }} className="rounded-xl bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700">Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" data-testid="confirm-delete-teacher">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-100 text-rose-600"><AlertTriangle size={18} /></div>
              <h3 className="text-xl font-bold text-slate-900">Delete "{confirmDel.full_name}"?</h3>
            </div>
            <p className="mt-3 text-sm text-slate-600">This will permanently remove the teacher account. Their authored chapters/sub-chapters and uploaded materials will remain available to students.</p>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setConfirmDel(null)} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={doDelete} data-testid="confirm-delete-teacher-confirm" className="rounded-xl bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-rose-700">Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {credView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" data-testid="creds-modal-teacher">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">Teacher Credentials</h3>
              <button onClick={() => setCredView(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <p className="mt-2 text-sm text-slate-600">{credView.full_name}</p>
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 font-mono text-sm">
              <p>Username: <span className="font-semibold">{credView.username}</span></p>
              <p>Password: <span className="font-semibold">{credView.password}</span></p>
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => { navigator.clipboard.writeText(`Username: ${credView.username}\nPassword: ${credView.password}`); toast.success("Copied"); }}
                className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Copy size={14} /> Copy
              </button>
              <button onClick={() => setCredView(null)} className="rounded-xl bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700">Close</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

const PALETTES = {
  violet: { on: "border-violet-400 bg-violet-50 text-violet-700", off: "border-slate-200 bg-white text-slate-600 hover:border-violet-200 hover:bg-violet-50/40" },
  sky:    { on: "border-sky-400 bg-sky-50 text-sky-700",          off: "border-slate-200 bg-white text-slate-600 hover:border-sky-200 hover:bg-sky-50/40" },
  amber:  { on: "border-amber-400 bg-amber-50 text-amber-700",    off: "border-slate-200 bg-white text-slate-600 hover:border-amber-200 hover:bg-amber-50/40" },
};

const ChipField = ({ label, options, values, onToggle, palette = "violet", testidPrefix = "chip" }) => {
  const p = PALETTES[palette] || PALETTES.violet;
  return (
    <div>
      <span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((v) => {
          const on = values.includes(v);
          return (
            <button
              type="button"
              key={v}
              onClick={() => onToggle(v)}
              data-testid={`${testidPrefix}-chip-${v}`}
              className={`rounded-full border-2 px-3 py-1 text-xs font-semibold transition ${on ? p.on : p.off}`}
            >
              {v}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const inp = "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100";
const Field = ({ label, children }) => (<label className="block"><span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>{children}</label>);
